import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef } from "react";
// Flag ref declared at module level so it persists across renders without needing to be in deps

import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isTaskRenderable } from "@/lib/taskRenderability";
import { mapDbTaskToTask } from "@/lib/taskMapper";
import { fetchPremiumTeaserRows, mapTeaserRowToTask } from "@/lib/premiumTeaser";
import { MAX_HISTORY_ITEMS, AUTO_LOAD_THRESHOLD } from "@/constants/app";
import { useRecordAttemptMutation } from "@/hooks/useUserProgress";
import { useProgressSync, useLoadProgressFromDatabase, saveToLocalHistory } from "@/hooks/useProgressSync";
import { useAuth } from "@/hooks/useAuth";
import { useDailyQuest } from "@/hooks/useDailyQuest";
import { 
  useTasksQuery, 
  useTaskImagesQuery, 
  useAddTaskMutation, 
  useUpdateTaskMutation, 
  useDeleteTaskMutation,
  taskKeys 
} from "@/hooks/useTasksQuery";
import { 
  useAddDocumentTypeMutation,
  useAddFormulaMutation,
  categoryKeys,
  documentTypeKeys,
  formulaKeys
} from "@/hooks/useCategoriesQuery";
import { useFlattenedTasks, TASKS_PAGE_SIZE } from "@/hooks/usePaginatedTasksQuery";
import { useBootstrapData } from "@/hooks/useBootstrapData";

export interface DropdownOption {
  label: string;
  isCorrect: boolean;
}

export interface TableCell {
  value: string;
  editable: boolean;
  correctAnswers?: string[];
  colspan?: number;
  rowspan?: number;
  merged?: boolean;
  mergedInto?: { row: number; col: number };
  cellType?: 'text' | 'dropdown';
  dropdownOptions?: DropdownOption[];
  dropdownMultiSelect?: boolean;
}

export interface TableData {
  headerRow: boolean;
  headerColumn: boolean;
  cells: TableCell[][];
  caseSensitive?: boolean; // Whether answer checking is case-sensitive
}

export interface MatchingData {
  letters: { label: string; value: string }[];
  middleText: string;
  numbers: { label: string; value: string }[];
  correctLetter: string;
  correctNumber: string;
}

export interface Task {
  id: string;
  slug?: string;
  question: string;
  instruction?: string;
  sources?: string;
  answers: string[];
  correctIndex: number;
  categories: string[];
  year?: number;
  documentType?: string;
  formula?: string;
  taskNumber?: number;
  subpoint?: number;
  imageUrls?: string[];
  imageThumbnails?: string[]; // Thumbnail URLs for mobile
  imageCompressed?: boolean[]; // Array indicating if each image was compressed
  taskType?: string;
  taskSubtype?: string;
  useYesNo?: boolean;
  modelAnswer?: string;
  prefilledAnswer?: string;
  operationalVerb?: string;
  tableData?: TableData;
  matchingData?: MatchingData;
  strikethroughText?: string;
  isTwoPoints?: boolean;
  maxPoints?: number; // 1, 2, or 3 points
  isMultiSelect?: boolean; // For ABCD tasks with multiple correct answers
  tableScoringMode?: 'rows' | 'columns' | 'both' | 'custom' | null;
  customScoringScenarios?: string[][]; // Array of scenarios, each scenario is array of cell keys like ["0-1", "1-2"]
  customScoringPoints?: number[]; // Points per custom scenario (1 or 2). Default 1 for backward compat.
  verdictOptions?: { options: string[]; correctIndex: number; correctIndices?: number[] };
  multiTableData?: MultiTableEntry[];
  isPremium?: boolean;
  instructionImageUrl?: string;
  instructionImageThumbnail?: string;
  imageDimensions?: Array<{ w: number; h: number }>;
  instructionImageWidth?: number;
  instructionImageHeight?: number;
  imageBlurData?: string[];
  instructionImageBlur?: string;
  /** Per-image flag: true when this image is a server-normalized PNG diagram
   *  (originally black background → white). Only these get dark-mode inversion. */
  imageIsNormalizedDiagram?: boolean[];
  /** Same flag for the standalone instruction image. */
  instructionImageIsNormalizedDiagram?: boolean;
  // Bilingual variants — for tasks under BILINGUAL_DOCUMENT_TYPES.
  // Convention: main fields (question, instruction, answers, ...) hold the EN ORIGINAL.
  // *_pl fields hold the Polish TRANSLATION (which may be missing — to be filled by admin/AI).
  questionPl?: string;
  instructionPl?: string;
  answersPl?: string[];
  tableDataPl?: TableData;
  multiTableDataPl?: MultiTableEntry[];
  matchingDataPl?: MatchingData;
  strikethroughTextPl?: string;
  prefilledAnswerPl?: string;
  sourcesPl?: string;
  verdictOptionsPl?: { options: string[] };
}

const EMPTY_TASKS_DATA: Task[] = [];

export interface MultiTableEntry {
  tableData: TableData;
  maxPoints: number;
  tableScoringMode?: 'rows' | 'columns' | 'both' | 'custom' | null;
  customScoringScenarios?: string[][];
  customScoringPoints?: number[];
  sequentialScoring?: boolean;
}

export interface Category {
  id: string;
  name: string;
  icon: string;
}

export interface DocumentType {
  id: string;
  name: string;
}

export interface Formula {
  id: string;
  name: string;
  color: string;
}

export interface HistoryEntry {
  task: Task;
  selectedIndex: number;
  correct: boolean;
  timestamp: Date;
}

interface CategoryStats {
  total: number;
  correct: number;
}

interface TaskContextType {
  tasks: Task[];
  currentTask: Task | null;
  nextTask: Task | null;
  history: HistoryEntry[];
  stats: { correct: number; total: number };
  categories: Category[];
  documentTypes: DocumentType[];
  formulas: Formula[];
  selectedCategories: string[];
  selectedYears: number[];
  selectedDocTypes: string[];
  selectedFormula: string | null;
  selectedOperationalVerbs: string[];
  selectedTaskTypes: string[];
  showPremiumOnly: boolean;
  homeworkTaskIds: string[] | null;
  imageFilter: 'all' | 'with' | 'without';
  isLoading: boolean;
  isFromHistory: boolean;
  // Set of task IDs that were completed correctly (from DB + session)
  completedTaskIds: Set<string>;
  // Pagination info
  totalTasksCount: number;
  loadedTasksCount: number;
  hasMoreTasks: boolean;
  isLoadingMoreTasks: boolean;
  loadMoreTasks: () => void;
  // Methods
  setSelectedCategories: (categories: string[]) => void;
  setSelectedYears: (years: number[]) => void;
  setSelectedDocTypes: (docTypes: string[]) => void;
  setSelectedFormula: (formula: string | null) => void;
  setSelectedOperationalVerbs: (verbs: string[]) => void;
  setSelectedTaskTypes: (taskTypes: string[]) => void;
  setShowPremiumOnly: (show: boolean) => void;
  setHomeworkTaskIds: (ids: string[] | null) => void;
  setImageFilter: (v: 'all' | 'with' | 'without') => void;
  toggleTaskType: (taskType: string) => void;
  toggleOperationalVerb: (verb: string) => void;
  toggleCategory: (category: string) => void;
  toggleYear: (year: number) => void;
  toggleDocType: (docType: string) => void;
  setCurrentTask: (task: Task | null) => void;
  setTaskFromHistory: (task: Task) => void;
  getTaskCountByCategory: (category: string) => number;
  getStatsByCategory: () => Record<string, CategoryStats>;
  getFilteredTasks: () => Task[];
  getAvailableYears: () => number[];
  getTaskById: (id: string) => Task | undefined;
  loadTaskById: (id: string) => Promise<boolean>;
  loadTaskImages: (taskId: string) => Promise<string[]>;
  addTask: (task: Omit<Task, "id">) => Promise<void>;
  updateTask: (id: string, task: Partial<Task>) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
  getRandomTask: () => void;
  submitAnswer: (index: number) => boolean;
  recordTrueFalseAnswer: (isCorrect: boolean) => void;
  markOpenTaskCompleted: (taskId: string) => void;
  markTasksCompleted: (taskIds: string[]) => void;
  resetProgress: () => void;
  refetchTasks: () => Promise<void>;
  refetchCategories: () => Promise<void>;
  refetchDocumentTypes: () => Promise<void>;
  refetchFormulas: () => Promise<void>;
  addDocumentType: (name: string) => Promise<void>;
  addFormula: (name: string, color: string) => Promise<void>;
}

const TaskContext = createContext<TaskContextType | undefined>(undefined);

export const TaskProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  
  // Auth for checking if user is logged in
  const { user, isAdmin, canManageTasks } = useAuth();

  // React Query hooks for data fetching with caching
  // Only admins load the full task list (needed for add/update/delete operations)
  // Regular users rely solely on paginated queries, saving ~50% initial data transfer
  const { data: tasksData = EMPTY_TASKS_DATA, isLoading: tasksLoading } = useTasksQuery({ enabled: canManageTasks });
  // Bootstrap RPC — single call replaces useCategoriesQuery, useDocumentTypesQuery, useFormulasQuery, useFilterCounts
  const { categories: categoriesData, documentTypes: documentTypesData, formulas: formulasData } = useBootstrapData();
  
  // Only load paginated tasks in random mode — list mode uses useListModeTasks independently
  // Use shared helper so mobile always starts in random mode (matching Index.tsx)
  const isRandomMode = (() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    if (isMobile) return true; // mobile always starts random
    const savedMode = typeof window !== 'undefined' ? localStorage.getItem("biozadania_view_mode") : null;
    return !savedMode || savedMode === 'random' || savedMode === 'exam';
  })();
  // paginatedQuery moved below state declarations (needs filter state)
  // Mutations
  const addTaskMutation = useAddTaskMutation();
  const updateTaskMutation = useUpdateTaskMutation();
  const deleteTaskMutation = useDeleteTaskMutation();
  const addDocumentTypeMutation = useAddDocumentTypeMutation();
  const addFormulaMutation = useAddFormulaMutation();
  const recordAttemptMutation = useRecordAttemptMutation();
  
  // (Auth moved above useTasksQuery to use canManageTasks for enabled flag)

  // Daily quest integration
  const { checkAndCompleteQuest } = useDailyQuest();

  // Progress sync hooks
  const { isSyncing } = useProgressSync();
  
  // Local state for tasks with images loaded
  const [tasksWithImages, setTasksWithImages] = useState<Task[]>([]);
  const [currentTask, setCurrentTask] = useState<Task | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [stats, setStats] = useState({ correct: 0, total: 0 });
  const [usedTaskIds, setUsedTaskIds] = useState<Set<string>>(new Set());
  const [correctTaskIds, setCorrectTaskIds] = useState<Set<string>>(new Set());
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [selectedDocTypes, setSelectedDocTypes] = useState<string[]>([]);
  const [selectedFormula, setSelectedFormula] = useState<string | null>(null);
  const [selectedOperationalVerbs, setSelectedOperationalVerbs] = useState<string[]>([]);
  // Initialize task type filters immediately (not in useEffect) so the first query already includes them
  const [selectedTaskTypes, setSelectedTaskTypes] = useState<string[]>(() => {
    const isMobileDevice = typeof window !== 'undefined' && window.innerWidth < 768;
    return isMobileDevice ? ["ABCD", "Prawda/Fałsz", "AB/123", "Tabelka", "Podkreślanka"] : [];
  });
  const [showPremiumOnly, setShowPremiumOnly] = useState(false);
  const [homeworkTaskIds, setHomeworkTaskIds] = useState<string[] | null>(null);
  const [imageFilter, setImageFilter] = useState<'all' | 'with' | 'without'>('all');
  const [isFromHistory, setIsFromHistory] = useState(false);
  const [prefetchedTask, setPrefetchedTask] = useState<Task | null>(null);
  const [isPrefetching, setIsPrefetching] = useState(false);

  // Paginated query for random mode — pass active filters so first batch already matches
  const paginatedQuery = useFlattenedTasks({ 
    enabled: isRandomMode && !canManageTasks,
    filters: {
      categories: selectedCategories.length > 0 ? selectedCategories : undefined,
      years: selectedYears.length > 0 ? selectedYears : undefined,
      docTypes: selectedDocTypes.length > 0 ? selectedDocTypes : undefined,
      formula: selectedFormula,
      operationalVerbs: selectedOperationalVerbs.length > 0 ? selectedOperationalVerbs : undefined,
      taskSubtypes: selectedTaskTypes.length > 0 ? selectedTaskTypes : undefined,
    },
  });

  // Load progress from database on login
  const handleProgressLoaded = useCallback((progress: Array<{ taskId: string; isCorrect: boolean }>) => {
    // Mark correctly answered tasks
    const correctIds = progress.filter(p => p.isCorrect).map(p => p.taskId);
    setCorrectTaskIds(new Set(correctIds));
    
    // Update stats
    const totalAnswered = progress.length;
    const totalCorrect = progress.filter(p => p.isCorrect).length;
    setStats({ correct: totalCorrect, total: totalAnswered });
  }, []);

  useLoadProgressFromDatabase(handleProgressLoaded);

  // Sync React Query data with local state (merge with images)
  useEffect(() => {
    setTasksWithImages(prev => {
      // Merge: keep images from prev if task exists there
      const prevMap = new Map(prev.map(t => [t.id, t]));
      return tasksData.map(task => {
        const existing = prevMap.get(task.id);
        if (existing?.imageUrls) {
          return { ...task, imageUrls: existing.imageUrls };
        }
        return task;
      });
    });
  }, [tasksData]);

  // Derived data from React Query
  // For admins, use full task list; for regular users, use paginated list
  const tasks = canManageTasks ? tasksWithImages : (paginatedQuery.tasks.length > 0 ? paginatedQuery.tasks : tasksWithImages);
  const categories = categoriesData;
  const documentTypes = documentTypesData;
  const formulas = formulasData;
  
  // Public data doesn't wait for auth — only admin full-list needs auth resolved
  // Before auth resolves, assume non-admin (paginated) — if admin, React Query will switch
  const isLoading = canManageTasks ? tasksLoading : paginatedQuery.isLoading;

  // Load images for a specific task (on demand)
  const loadTaskImages = useCallback(async (taskId: string): Promise<string[]> => {
    const { data, error } = await supabase
      .from("tasks")
      .select("image_urls, image_url, image_thumbnails")
      .eq("id", taskId)
      .maybeSingle();

    if (error || !data) {
      console.error("Error loading task images:", error);
      return [];
    }

    return data.image_urls || (data.image_url ? [data.image_url] : []);
  }, []);

  // Refetch functions using React Query
  const refetchTasks = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
  }, [queryClient]);

  const refetchCategories = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
  }, [queryClient]);

  const refetchDocumentTypes = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: documentTypeKeys.lists() });
  }, [queryClient]);

  const refetchFormulas = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: formulaKeys.lists() });
  }, [queryClient]);

  // Add document type using mutation
  const addDocumentType = useCallback(async (name: string) => {
    await addDocumentTypeMutation.mutateAsync(name);
  }, [addDocumentTypeMutation]);

  // Add formula using mutation
  const addFormula = useCallback(async (name: string, color: string) => {
    await addFormulaMutation.mutateAsync({ name, color });
  }, [addFormulaMutation]);

  // Get task count by category
  const getTaskCountByCategory = useCallback(
    (category: string) => tasks.filter((t) => t.categories?.includes(category)).length,
    [tasks]
  );

  // Get stats by category from history - counting unique tasks only
  const getStatsByCategory = useCallback((): Record<string, CategoryStats> => {
    const categoryStats: Record<string, CategoryStats> = {};
    const processedTasks: Record<string, { category: string; wasCorrect: boolean }> = {};
    
    // Process history in reverse (oldest first) to get final state of each task
    [...history].reverse().forEach((entry) => {
      const taskId = entry.task.id;
      const cat = entry.task.categories?.[0] || "";
      
      // Track each unique task with its latest correct status
      processedTasks[taskId] = {
        category: cat,
        wasCorrect: entry.correct || processedTasks[taskId]?.wasCorrect || false,
      };
    });
    
    // Build stats from unique tasks
    Object.values(processedTasks).forEach(({ category, wasCorrect }) => {
      if (!categoryStats[category]) {
        categoryStats[category] = { total: 0, correct: 0 };
      }
      categoryStats[category].total += 1;
      if (wasCorrect) {
        categoryStats[category].correct += 1;
      }
    });
    
    return categoryStats;
  }, [history]);

  // Filter tasks by selected categories, years, document types, formula, and task type
  // OPTIMIZATION: For non-admin users with server-filtered paginated data, skip redundant client filtering
  const filteredTasks = useMemo(() => {
    // FIX Problem 2: homework filter — restrict to assigned tasks when homework deep-link is active
    let result = tasks;
    if (homeworkTaskIds && homeworkTaskIds.length > 0) {
      result = result.filter(t => homeworkTaskIds.includes(t.id));
    }

    // OPTIMIZATION Problem 4: paginated data is already server-filtered — just check renderability
    if (!canManageTasks && paginatedQuery.tasks.length > 0 && !homeworkTaskIds) {
      return paginatedQuery.tasks.filter(t => isTaskRenderable(t));
    }

    if (selectedCategories.length > 0) {
      result = result.filter((t) => 
        selectedCategories.some(cat => t.categories?.includes(cat))
      );
    }
    if (showPremiumOnly) {
      // Premium tasks don't have year/docType/formula — skip those filters
      result = result.filter((t) => t.isPremium);
    } else {
      if (selectedYears.length > 0) {
        result = result.filter((t) => t.year && selectedYears.includes(t.year));
      }
      if (selectedDocTypes.length > 0) {
        result = result.filter((t) => t.documentType && selectedDocTypes.includes(t.documentType));
      }
      if (selectedFormula) {
        result = result.filter((t) => t.formula === selectedFormula);
      }
    }
    if (selectedOperationalVerbs.length > 0) {
      result = result.filter((t) => {
        if (!t.operationalVerb) return false;
        const taskVerbs = t.operationalVerb.split(',').map(v => v.trim());
        return taskVerbs.some(v => selectedOperationalVerbs.includes(v));
      });
    }
    if (selectedTaskTypes.length > 0) {
      result = result.filter((t) => {
        if (!t.taskSubtype) return false;
        const normalizedSubtype = t.taskSubtype === "Pole odpowiedzi" ? "Zadanie otwarte" : t.taskSubtype;
        return selectedTaskTypes.includes(normalizedSubtype);
      });
    }
    return result;
  }, [tasks, selectedCategories, selectedYears, selectedDocTypes, selectedFormula, selectedOperationalVerbs, selectedTaskTypes, showPremiumOnly, homeworkTaskIds, canManageTasks, paginatedQuery.tasks]);

  // Get available years from tasks
  const getAvailableYears = useCallback(() => {
    const years = [...new Set(tasks.filter(t => t.year).map(t => t.year!))];
    return years.sort((a, b) => b - a);
  }, [tasks]);

  const getFilteredTasks = useCallback(() => filteredTasks, [filteredTasks]);

  // Get task by ID
  const getTaskById = useCallback((id: string) => {
    return tasks.find(t => t.id === id);
  }, [tasks]);

  // Ref: when true, the auto-switch effect (filter mismatch) will NOT override currentTask.
  // Set in loadTaskById, cleared in getRandomTask / filter changes.
  const isDirectTaskLoadRef = useRef(false);

  // Load a specific task by ID or slug (sets it as current)
  const loadTaskById = useCallback(async (id: string): Promise<boolean> => {
    // Mark that we're loading a specific task — protect it from auto-switch
    isDirectTaskLoadRef.current = true;

    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

    // First check if task is in local state
    const localTask = tasks.find(t => isUUID ? t.id === id : t.slug === id);
    if (localTask) {
      setCurrentTask(localTask);
      setIsFromHistory(false);
      return true;
    }

    // Always try to fetch from database if not found locally
    const query = supabase.from("tasks").select("*");
    const { data, error } = await (isUUID ? query.eq("id", id) : query.eq("slug", id)).single();

    if (error || !data) {
      // PREMIUM: locked premium tasks are hidden by RLS. Fall back to the
      // content-free teaser so the paywall card can still be shown.
      try {
        const teaserRows = await fetchPremiumTeaserRows();
        const teaser = teaserRows.find((r) => (isUUID ? r.id === id : r.slug === id));
        if (teaser) {
          setCurrentTask(mapTeaserRowToTask(teaser));
          setIsFromHistory(false);
          return true;
        }
      } catch (teaserError) {
        console.error("Error fetching premium teaser task:", teaserError);
      }

      console.error("Error fetching task by ID:", error);
      isDirectTaskLoadRef.current = false;
      return false;
    }

    const task = mapDbTaskToTask(data);

    setCurrentTask(task);
    setIsFromHistory(false);
    return true;
  }, [tasks, isLoading]);

  // Closed task subtypes for "first task" preference
  const CLOSED_SUBTYPES = ["ABCD", "Prawda/Fałsz", "AB/123", "Tabelka", "Podkreślanka"];
  const isClosedType = useCallback((t: Task) => {
    const sub = t.taskSubtype;
    return sub ? CLOSED_SUBTYPES.includes(sub) : false;
  }, []);

  // FIX Problem 3: getRandomTask is now SYNCHRONOUS — sets task immediately, images load in background
  const getRandomTask = useCallback(() => {
    // Reset direct-load flag — user is now browsing normally
    isDirectTaskLoadRef.current = false;
    // Don't clear the current task if tasks are still loading – avoid blank card flash
    const isAnyLoading = isLoading || paginatedQuery.isLoadingMore;
    if (filteredTasks.length === 0) {
      if (!isAnyLoading) {
        setCurrentTask(null);
        setPrefetchedTask(null);
      }
      return;
    }

    // Check if we have a prefetched task ready that:
    // 1. Is not in correctTaskIds
    // 2. Still matches current filters (exists in filteredTasks)
    const prefetchedIsValid = prefetchedTask && 
      !correctTaskIds.has(prefetchedTask.id) && 
      filteredTasks.some(t => t.id === prefetchedTask.id);
    
    if (prefetchedIsValid) {
      setCurrentTask(prefetchedTask);
      setIsFromHistory(false);
      setUsedTaskIds((prev) => new Set([...prev, prefetchedTask.id]));
      setPrefetchedTask(null);
      return;
    }
    
    // Clear invalid prefetched task
    if (prefetchedTask && !prefetchedIsValid) {
      setPrefetchedTask(null);
    }

    // Exclude correctly answered tasks, current task, AND non-renderable tasks
    const currentId = currentTask?.id;
    const availableTasks = filteredTasks.filter((t) => 
      !correctTaskIds.has(t.id) && t.id !== currentId && isTaskRenderable(t)
    );
    
    if (availableTasks.length === 0) {
      // All tasks answered correctly - show completion message
      setCurrentTask(null);
      setPrefetchedTask(null);
      return;
    }

    // Further filter out used tasks (to avoid repeats until all remaining are used)
    let unusedTasks = availableTasks.filter((t) => !usedTaskIds.has(t.id));
    
    if (unusedTasks.length === 0) {
      // Reset used IDs but keep correct ones excluded
      setUsedTaskIds(new Set());
      unusedTasks = availableTasks;
    }

    // For the very first task (nothing shown yet), prefer closed types for better UX
    let pool = unusedTasks;
    if (usedTaskIds.size === 0 && !currentTask) {
      const closedTasks = unusedTasks.filter(isClosedType);
      if (closedTasks.length > 0) {
        pool = closedTasks;
      }
    }

    const randomIndex = Math.floor(Math.random() * pool.length);
    const selected = pool[randomIndex];
    
    // Set task IMMEDIATELY — no waiting for images (Problem 3 fix)
    setCurrentTask(selected);
    setIsFromHistory(false);
    setUsedTaskIds((prev) => new Set([...prev, selected.id]));
  }, [filteredTasks, usedTaskIds, correctTaskIds, prefetchedTask, currentTask?.id, isLoading, paginatedQuery.isLoadingMore, isClosedType]);

  // Background image loader: when currentTask changes and has no images, load them
  useEffect(() => {
    if (!currentTask?.id) return;
    // Already has images loaded — skip
    if (currentTask.imageUrls && currentTask.imageUrls.length > 0) return;
    
    let cancelled = false;
    loadTaskImages(currentTask.id).then(imageUrls => {
      if (cancelled) return;
      if (imageUrls && imageUrls.length > 0) {
        setCurrentTask(prev => 
          prev?.id === currentTask.id 
            ? { ...prev, imageUrls }
            : prev
        );
        // Also update the tasks cache
        setTasksWithImages(prev => prev.map(t => 
          t.id === currentTask.id ? { ...t, imageUrls } : t
        ));
      }
    }).catch(err => {
      if (!cancelled) console.warn('Background image load failed:', err);
    });
    
    return () => { cancelled = true; };
  }, [currentTask?.id, loadTaskImages]);

  // Prefetch next task images in background
  const prefetchNextTask = useCallback(async () => {
    if (isPrefetching) return;
    
    // Get available tasks excluding current, used, and correct
    const currentId = currentTask?.id;
    let availableTasks = filteredTasks.filter((t) => 
      !correctTaskIds.has(t.id) && 
      !usedTaskIds.has(t.id) && 
      t.id !== currentId
    );
    
    if (availableTasks.length === 0) {
      // Try resetting used IDs
      availableTasks = filteredTasks.filter((t) => 
        !correctTaskIds.has(t.id) && 
        t.id !== currentId
      );
    }
    
    if (availableTasks.length === 0) return;

    setIsPrefetching(true);
    
    try {
      const randomIndex = Math.floor(Math.random() * availableTasks.length);
      const nextTask = availableTasks[randomIndex];
      
      // Load images if not already loaded
      let taskWithImages = nextTask;
      if (!nextTask.imageUrls) {
        try {
          const imageUrls = await loadTaskImages(nextTask.id);
          taskWithImages = { ...nextTask, imageUrls: imageUrls && imageUrls.length > 0 ? imageUrls : undefined };
          // Update the task in the tasks array
          setTasksWithImages(prev => prev.map(t => t.id === nextTask.id ? taskWithImages : t));
        } catch (err) {
          // AbortError or network error – prefetch without images, still usable
          console.warn('Prefetch image load failed, continuing without images:', err);
        }
      }
      
      setPrefetchedTask(taskWithImages);
    } catch (error) {
      console.error('Prefetch failed:', error);
    } finally {
      setIsPrefetching(false);
    }
  }, [filteredTasks, usedTaskIds, correctTaskIds, currentTask, loadTaskImages, isPrefetching]);

  // Auto-prefetch next task whenever currentTask changes
  useEffect(() => {
    // Only prefetch if we have a current task and no prefetched task yet
    if (!currentTask || prefetchedTask || isPrefetching) {
      return;
    }
    
    // Small delay to ensure current task is fully loaded first
    const timer = setTimeout(() => {
      prefetchNextTask();
    }, 800);
    
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTask?.id]); // Only re-run when task ID changes, not on every prefetchedTask change

  // Auto-load all remaining tasks in background after initial page loads
  const loadMoreRef = useRef(paginatedQuery.loadMore);
  loadMoreRef.current = paginatedQuery.loadMore;
  const backgroundLoadTriggered = useRef(false);

  // Reset one-shot background loader whenever filters/scope change
  useEffect(() => {
    backgroundLoadTriggered.current = false;
  }, [
    selectedCategories,
    selectedYears,
    selectedDocTypes,
    selectedFormula,
    selectedOperationalVerbs,
    selectedTaskTypes,
    showPremiumOnly,
    homeworkTaskIds,
  ]);
  
  useEffect(() => {
    if (isAdmin) return;
    if (backgroundLoadTriggered.current) return;
    if (paginatedQuery.isLoadingMore || !paginatedQuery.hasMore) return;
    // Only trigger once we have the initial page loaded
    if (paginatedQuery.tasks.length === 0) return;
    
    backgroundLoadTriggered.current = true;
    const timeoutId = setTimeout(() => loadMoreRef.current(), 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginatedQuery.tasks.length, paginatedQuery.hasMore, isAdmin]);

  // Auto-switch to a matching task when filters change and current task doesn't match.
  // Uses useLayoutEffect to run BEFORE paint — prevents 1-frame flash of stale content on mobile.
  const lastAutoSwitchFilterRef = useRef<string>("");

  useLayoutEffect(() => {
    // Skip if no current task or no filters applied
    if (!currentTask) return;
    // Skip if a specific task was loaded via deep link (loadTaskById)
    if (isDirectTaskLoadRef.current) return;
    
    // Check if current task matches current filters
    const currentTaskMatchesFilters = filteredTasks.some(t => t.id === currentTask.id);
    
    // If current task doesn't match filters, get a new random task
    if (!currentTaskMatchesFilters && filteredTasks.length > 0) {
      // Anti-loop: don't re-trigger if we just switched for the same filter state
      const filterFingerprint = `${selectedCategories.join(",")}|${selectedYears.join(",")}|${selectedDocTypes.join(",")}|${selectedFormula}|${selectedOperationalVerbs.join(",")}|${selectedTaskTypes.join(",")}|${currentTask.id}`;
      if (lastAutoSwitchFilterRef.current === filterFingerprint) return;
      lastAutoSwitchFilterRef.current = filterFingerprint;
      
      getRandomTask();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTasks]); // React when filtered tasks change

  const setTaskFromHistory = useCallback((task: Task) => {
    // Set task immediately — images will load in background via the useEffect
    setCurrentTask(task);
    setIsFromHistory(true);
  }, []);

  // Reset used tasks and prefetched task when filters change
  const handleSetSelectedCategories = useCallback((categories: string[]) => {
    setSelectedCategories(categories);
    setUsedTaskIds(new Set());
    setPrefetchedTask(null);
  }, []);

  const handleSetSelectedYears = useCallback((years: number[]) => {
    setSelectedYears(years);
    setUsedTaskIds(new Set());
    setPrefetchedTask(null);
  }, []);

  const handleSetSelectedDocTypes = useCallback((docTypes: string[]) => {
    setSelectedDocTypes(docTypes);
    setUsedTaskIds(new Set());
    setPrefetchedTask(null);
  }, []);

  const handleSetSelectedFormula = useCallback((formula: string | null) => {
    setSelectedFormula(formula);
    setUsedTaskIds(new Set());
    setPrefetchedTask(null);
  }, []);

  const handleSetSelectedOperationalVerbs = useCallback((verbs: string[]) => {
    setSelectedOperationalVerbs(verbs);
    setUsedTaskIds(new Set());
    setPrefetchedTask(null);
  }, []);

  const handleSetSelectedTaskTypes = useCallback((taskTypes: string[]) => {
    setSelectedTaskTypes(taskTypes);
    setUsedTaskIds(new Set());
    setPrefetchedTask(null);
  }, []);

  const handleToggleOperationalVerb = useCallback((verb: string) => {
    setSelectedOperationalVerbs(prev => 
      prev.includes(verb) ? prev.filter(v => v !== verb) : [...prev, verb]
    );
    setUsedTaskIds(new Set());
    setPrefetchedTask(null);
  }, []);

  const handleToggleTaskType = useCallback((taskType: string) => {
    setSelectedTaskTypes(prev => 
      prev.includes(taskType) ? prev.filter(t => t !== taskType) : [...prev, taskType]
    );
    setUsedTaskIds(new Set());
    setPrefetchedTask(null);
  }, []);

  // Toggle functions for multi-select
  const toggleCategory = useCallback((category: string) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
    setUsedTaskIds(new Set());
    setPrefetchedTask(null);
  }, []);

  const toggleYear = useCallback((year: number) => {
    setSelectedYears(prev => 
      prev.includes(year) 
        ? prev.filter(y => y !== year)
        : [...prev, year]
    );
    setUsedTaskIds(new Set());
    setPrefetchedTask(null);
  }, []);

  const toggleDocType = useCallback((docType: string) => {
    setSelectedDocTypes(prev => 
      prev.includes(docType) 
        ? prev.filter(d => d !== docType)
        : [...prev, docType]
    );
    setUsedTaskIds(new Set());
    setPrefetchedTask(null);
  }, []);

  // FIX Problem 5: Shared helper for recording answers (deduplicates submitAnswer + recordTrueFalseAnswer)
  const recordAnswer = useCallback((taskId: string, task: Task, isCorrect: boolean, selectedIndex: number) => {
    setHistory((prev) => [
      {
        task,
        selectedIndex,
        correct: isCorrect,
        timestamp: new Date(),
      },
      ...prev,
    ].slice(0, MAX_HISTORY_ITEMS));

    setStats((prev) => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1,
    }));

    if (isCorrect) {
      setCorrectTaskIds((prev) => new Set([...prev, taskId]));
    }

    saveToLocalHistory(taskId, isCorrect);

    if (user) {
      recordAttemptMutation.mutate({ taskId, isCorrect });
    }

    if (isCorrect) {
      checkAndCompleteQuest(taskId);
    }

    prefetchNextTask();
  }, [user, recordAttemptMutation, checkAndCompleteQuest, prefetchNextTask]);

  const submitAnswer = useCallback(
    (index: number): boolean => {
      if (!currentTask) return false;
      const correct = index === currentTask.correctIndex;
      recordAnswer(currentTask.id, currentTask, correct, index);
      return correct;
    },
    [currentTask, recordAnswer]
  );

  // Record True/False answer to history (for non-ABCD tasks)
  const recordTrueFalseAnswer = useCallback((isCorrect: boolean) => {
    if (!currentTask) return;
    recordAnswer(currentTask.id, currentTask, isCorrect, -1);
  }, [currentTask, recordAnswer]);

  // Add task using mutation with optimistic update
  const addTask = useCallback(async (task: Omit<Task, "id">) => {
    await addTaskMutation.mutateAsync(task);
  }, [addTaskMutation]);

  // Update task using mutation with optimistic update
  const updateTask = useCallback(async (id: string, updates: Partial<Task>) => {
    await updateTaskMutation.mutateAsync({ id, updates });
  }, [updateTaskMutation]);

  // Delete task using mutation with optimistic update
  const deleteTask = useCallback(async (id: string) => {
    await deleteTaskMutation.mutateAsync(id);
  }, [deleteTaskMutation]);

  // Mark open task as completed (when user saves their response or completes table task)
  const markOpenTaskCompleted = useCallback((taskId: string) => {
    // Add to correctly answered tasks immediately
    setCorrectTaskIds((prev) => new Set([...prev, taskId]));
    
    // Also add to used task IDs to prevent immediate re-selection
    setUsedTaskIds((prev) => new Set([...prev, taskId]));
    
    // Clear prefetched task if it was this task
    if (prefetchedTask?.id === taskId) {
      setPrefetchedTask(null);
    }
    
    // Also add to history as "answered"
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      setHistory((prev) => {
        const newEntry: HistoryEntry = {
          task,
          correct: true, // Open tasks with response are considered "completed"
          selectedIndex: -1, // N/A for open tasks
          timestamp: new Date(),
        };
        const newHistory = [newEntry, ...prev].slice(0, MAX_HISTORY_ITEMS);
        return newHistory;
      });
    }

    // Check if this was today's daily quest
    checkAndCompleteQuest(taskId);
  }, [tasks, prefetchedTask, checkAndCompleteQuest]);

  // Mark multiple tasks as completed (used by exam mode)
  const markTasksCompleted = useCallback((taskIds: string[]) => {
    if (taskIds.length === 0) return;
    
    setCorrectTaskIds((prev) => {
      const updated = new Set(prev);
      taskIds.forEach(id => updated.add(id));
      return updated;
    });
    
    // Update stats
    setStats((prev) => ({
      correct: prev.correct + taskIds.length,
      total: prev.total + taskIds.length,
    }));
  }, []);

  const resetProgress = useCallback(() => {
    setHistory([]);
    setStats({ correct: 0, total: 0 });
    setUsedTaskIds(new Set());
    setCorrectTaskIds(new Set());
    setCurrentTask(null);
    setPrefetchedTask(null);
  }, []);

  return (
    <TaskContext.Provider
      value={{
        tasks,
        currentTask,
        nextTask: prefetchedTask,
        history,
        stats,
        categories,
        documentTypes,
        formulas,
        selectedCategories,
        selectedYears,
        selectedDocTypes,
        selectedFormula,
        selectedOperationalVerbs,
        selectedTaskTypes,
        showPremiumOnly,
        homeworkTaskIds,
        imageFilter,
        isLoading,
        isFromHistory,
        completedTaskIds: correctTaskIds,
        // Pagination info
        totalTasksCount: paginatedQuery.totalCount,
        loadedTasksCount: paginatedQuery.loadedCount,
        hasMoreTasks: paginatedQuery.hasMore ?? false,
        isLoadingMoreTasks: paginatedQuery.isLoadingMore,
        loadMoreTasks: () => paginatedQuery.loadMore(),
        // Methods
        setSelectedCategories: handleSetSelectedCategories,
        setSelectedYears: handleSetSelectedYears,
        setSelectedDocTypes: handleSetSelectedDocTypes,
        setSelectedFormula: handleSetSelectedFormula,
        setSelectedOperationalVerbs: handleSetSelectedOperationalVerbs,
        setSelectedTaskTypes: handleSetSelectedTaskTypes,
        setShowPremiumOnly,
        setHomeworkTaskIds,
        setImageFilter,
        toggleTaskType: handleToggleTaskType,
        toggleOperationalVerb: handleToggleOperationalVerb,
        toggleCategory,
        toggleYear,
        toggleDocType,
        setCurrentTask,
        setTaskFromHistory,
        getTaskCountByCategory,
        getStatsByCategory,
        getFilteredTasks,
        getAvailableYears,
        getTaskById,
        loadTaskById,
        loadTaskImages,
        addTask,
        updateTask,
        deleteTask,
        getRandomTask,
        submitAnswer,
        recordTrueFalseAnswer,
        markOpenTaskCompleted,
        markTasksCompleted,
        resetProgress,
        refetchTasks,
        refetchCategories,
        refetchDocumentTypes,
        refetchFormulas,
        addDocumentType,
        addFormula,
      }}
    >
      {children}
    </TaskContext.Provider>
  );
};

export const useTask = () => {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error("useTask must be used within a TaskProvider");
  }
  return context;
};
